/**
 * Sample data on / off - the switch, the banner and the first-open choice.
 * (V19.24, WorkOBay's handover "Sample data", Ivan 1 Nov 2026)
 *
 * The server builds and removes the sample (backend/sample_data.py). This file
 * only asks for it and shows that it is on:
 *
 *   - SampleBanner: a small badge on every screen while it is on. The owner
 *     can switch it off from there (one tap, then confirm).
 *   - SampleSwitchRow: Settings, always there for the owner.
 *   - SampleChoice: the empty Lists screen - "Explore with sample data" or
 *     "Start with my own".
 *
 * ONLY THE OWNER switches it. A household shares one account and a phone with
 * a member name belongs to that member (Settings' isAccountOwner rule); the
 * member name is sent with the request, and the server refuses a member.
 *
 * After a switch the app reloads on Lists, so every screen reads the
 * household's data afresh - lists, Stats, Meals and Recipes all fetch their
 * own, and a reload is the one way to be sure none keeps a stale copy.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { useTranslation, getLanguage, getCurrencyCode } from "@/i18n";
import { localZoneName } from "../../utils/localZone";
import { safeGet } from "../../utils/safeStorage";

export const SAMPLE_CHANGED = "xelanti-sample-changed";

export const isOwnerPhone = () => !safeGet("memberName");

let cached = null;

export function useSampleData() {
  const [state, setState] = useState(cached || { on: false, loaded: false });
  useEffect(() => {
    let live = true;
    const load = () => axios.get(`${API}/sample-data`)
      .then((r) => { cached = { on: !!r.data?.on, loaded: true }; if (live) setState(cached); })
      .catch(() => { if (live) setState((s) => ({ ...s, loaded: true })); });
    load();
    window.addEventListener(SAMPLE_CHANGED, load);
    return () => { live = false; window.removeEventListener(SAMPLE_CHANGED, load); };
  }, []);
  return state;
}

export async function switchSample(on) {
  const body = {
    lang: getLanguage(),
    tz: localZoneName() || undefined,
    tz_offset: new Date().getTimezoneOffset(),
    currency: getCurrencyCode() || undefined,
    member_name: safeGet("memberName") || undefined,
  };
  const r = await axios.post(`${API}/sample-data/${on ? "on" : "off"}`, body);
  cached = { on, loaded: true };
  window.dispatchEvent(new Event(SAMPLE_CHANGED));
  return r.data;
}

function useSwitch() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  const run = async (on) => {
    if (busy) return;
    setBusy(on ? "on" : "off");
    const id = toast.loading(t(on ? "sample.adding" : "sample.removing"));
    try {
      await switchSample(on);
      toast.success(t(on ? "sample.on" : "sample.off"), { id });
      setTimeout(() => window.location.assign("/"), 400);
    } catch (e) {
      toast.error(t("sample.failed"), { id });
      setBusy(null);
    }
  };
  return { busy, run };
}

export function SampleOffConfirm({ onCancel, onConfirm, busy }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/50 z-[120] flex items-center justify-center px-4" onClick={onCancel}
      data-testid="sample-off-backdrop">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 w-full max-w-sm shadow-xl" role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()} data-testid="sample-off-dialog">
        <h3 className="text-lg font-bold mb-2">{t("sample.offTitle")}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">{t("sample.offBody")}</p>
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} disabled={!!busy}
            className="flex-1 py-3 rounded-full font-semibold bg-gray-200 dark:bg-gray-700" data-testid="sample-off-cancel">
            {t("common.cancel")}
          </button>
          <button type="button" onClick={onConfirm} disabled={!!busy}
            className="flex-1 py-3 rounded-full font-semibold bg-black text-white dark:bg-white dark:text-black disabled:opacity-60"
            data-testid="sample-off-confirm">
            {t("sample.switchOff")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The badge on every screen while the sample is on. */
export function SampleBanner() {
  const { t } = useTranslation();
  const { on } = useSampleData();
  const { busy, run } = useSwitch();
  const [ask, setAsk] = useState(false);
  if (!on) return null;
  const owner = isOwnerPhone();
  return (
    <>
      <div className="sample-banner" role="status" aria-label={t("sample.bannerLong")} data-testid="sample-banner">
        <span className="sample-banner-dot" aria-hidden="true" />
        <span>{t("sample.chip")}</span>
        {owner && (
          <button type="button" onClick={() => setAsk(true)} data-testid="sample-banner-off">
            {t("sample.switchOff")}
          </button>
        )}
      </div>
      {ask && <SampleOffConfirm busy={busy} onCancel={() => setAsk(false)} onConfirm={() => { setAsk(false); run(false); }} />}
    </>
  );
}

/** Settings: the switch, for the owner. */
export function SampleSwitchRow() {
  const { t } = useTranslation();
  const { on, loaded } = useSampleData();
  const { busy, run } = useSwitch();
  const [ask, setAsk] = useState(false);
  if (!isOwnerPhone()) return null;
  return (
    <>
      <div className="list-item" data-testid="sample-data-row">
        <span className="text-xl" aria-hidden="true">🧪</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium">{t("sample.setting")}</p>
          <p className="text-sm text-gray-400">{t("sample.settingDesc")}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!on}
          aria-label={t("sample.setting")}
          disabled={!loaded || !!busy}
          onClick={() => (on ? setAsk(true) : run(true))}
          className={`w-12 h-7 rounded-full transition-colors relative flex-shrink-0 disabled:opacity-50 ${on ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"}`}
          data-testid="sample-data-toggle"
        >
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      {ask && <SampleOffConfirm busy={busy} onCancel={() => setAsk(false)} onConfirm={() => { setAsk(false); run(false); }} />}
    </>
  );
}

/** The empty Lists screen: see the app with sample data, or start for real. */
export function SampleChoice({ onStartOwn }) {
  const { t } = useTranslation();
  const { on, loaded } = useSampleData();
  const { busy, run } = useSwitch();
  if (!loaded || on || !isOwnerPhone()) return null;
  return (
    <div className="sample-choice" data-testid="sample-choice">
      <p className="sample-choice-sub">{t("sample.exploreSub")}</p>
      <div className="flex flex-col gap-2 w-full max-w-xs mx-auto">
        <button type="button" onClick={() => run(true)} disabled={!!busy}
          className="py-3 px-5 rounded-full font-semibold bg-black text-white dark:bg-white dark:text-black disabled:opacity-60"
          data-testid="sample-explore">
          {t("sample.explore")}
        </button>
        <button type="button" onClick={onStartOwn} disabled={!!busy}
          className="py-3 px-5 rounded-full font-semibold border border-gray-300 dark:border-gray-600"
          data-testid="sample-start-own">
          {t("sample.startOwn")}
        </button>
      </div>
    </div>
  );
}
